import type {
  CreateLeadRequest, CreateLeadResponse, EnrollmentRequest, ProgressSyncRequest,
  ReadinessScore, ReportQuestionRequest, StatePack, StateCode
} from "@cdl/shared-types";

export interface InsightsOverview {
  totals: { users: number; leads: number; enrollments: number };
  active: { dau: number; wau: number };
  readinessDistribution: Record<string, number>;
  byState: { state: string; users: number }[];
}

export interface InsightsFunnel {
  stages: { key: string; label: string; count: number }[];
}

export interface InsightsQuestions {
  questions: {
    id: string;
    stem: string;
    testType: string;
    stateCode: string;
    attempts: number;
    correct: number;
    passRate: number | null;
    reports: number;
  }[];
}

export interface InsightsHealth {
  hubspotQueue: { pending: number; processed: number; failed: number };
  livePacks: {
    stateCode: string;
    version: number;
    publishedAt: string | null;
    sections: number;
    questions: number;
  }[];
}

export interface UserProfile {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  zip: string | null;
  state: string | null;
}

export class ApiError extends Error {
  readonly name = "ApiError";
  constructor(public readonly status: number, public readonly code: string, public readonly details?: unknown) {
    super(`api ${status} ${code}`);
  }
}

export interface ApiClientOpts {
  baseUrl: string;
  fetch?: typeof fetch;
  getAuthToken?: () => string | null;
}

export interface ApiClient {
  createLead(req: CreateLeadRequest): Promise<CreateLeadResponse>;
  getPack(state: StateCode, version?: number): Promise<StatePack>;
  getReadiness(): Promise<ReadinessScore>;
  submitEnrollment(req: EnrollmentRequest): Promise<{ ok: true }>;
  reportQuestion(req: ReportQuestionRequest): Promise<{ ok: true }>;
  syncProgress(req: ProgressSyncRequest): Promise<{ applied: number }>;
  deleteUser(): Promise<{ ok: true }>;
  getInsightsOverview(token: string): Promise<InsightsOverview>;
  getInsightsFunnel(token: string): Promise<InsightsFunnel>;
  getInsightsQuestions(token: string): Promise<InsightsQuestions>;
  getInsightsHealth(token: string): Promise<InsightsHealth>;
  getCurrentUser(): Promise<UserProfile>;
}

export function apiClient(opts: ApiClientOpts): ApiClient {
  const fetchFn = opts.fetch ?? fetch;

  async function request<T>(path: string, init: RequestInit & { json?: unknown }): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(init.headers as Record<string, string> | undefined)
    };
    const token = opts.getAuthToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetchFn(`${opts.baseUrl}${path}`, {
      ...init, headers,
      // Bypass browser HTTP cache. Our API owns caching semantics via
      // Cache-Control + ETag; disk cache layered on top has caused stale
      // bodies (see pack endpoint — stable etag during early-stage seeding).
      cache: "no-store",
      body: init.json !== undefined ? JSON.stringify(init.json) : init.body
    });

    let body: unknown = undefined;
    try { body = await res.json(); } catch {}

    if (!res.ok) {
      const err = body as { error?: string; details?: unknown } | undefined;
      throw new ApiError(res.status, err?.error ?? "unknown_error", err?.details);
    }
    return body as T;
  }

  return {
    createLead: (req) => request<CreateLeadResponse>("/api/leads", { method: "POST", json: req }),
    getPack: (state, version) => request<StatePack>(version ? `/api/packs/${state}/${version}` : `/api/packs/${state}`, { method: "GET" }),
    getReadiness: () => request<ReadinessScore>("/api/progress/readiness", { method: "GET" }),
    submitEnrollment: (req) => request<{ ok: true }>("/api/enrollment", { method: "POST", json: req }),
    reportQuestion: (req) => request<{ ok: true }>("/api/questions/report", { method: "POST", json: req }),
    syncProgress: (req) => request<{ applied: number }>("/api/progress/sync", { method: "POST", json: req }),
    deleteUser: () => request<{ ok: true }>("/api/users/delete", { method: "POST" }),
    getInsightsOverview: (token) =>
      request<InsightsOverview>(`/api/admin/insights/overview?token=${encodeURIComponent(token)}`, { method: "GET" }),
    getInsightsFunnel: (token) =>
      request<InsightsFunnel>(`/api/admin/insights/funnel?token=${encodeURIComponent(token)}`, { method: "GET" }),
    getInsightsQuestions: (token) =>
      request<InsightsQuestions>(`/api/admin/insights/questions?token=${encodeURIComponent(token)}`, { method: "GET" }),
    getInsightsHealth: (token) =>
      request<InsightsHealth>(`/api/admin/insights/health?token=${encodeURIComponent(token)}`, { method: "GET" }),
    getCurrentUser: () => request<UserProfile>("/api/users/me", { method: "GET" })
  };
}
